import React, { useState, useEffect, useRef } from "react";
import api from "../../config/configApi";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { BsChevronDown } from "react-icons/bs";
import { useNavigate } from "react-router-dom"; // Importe aqui

interface TabelaDeAvaliacaoProps {
  criterios: {
    nome: string;
    opcoes: string[];
  }[];
  nomePaciente: string;
  beneficiarioInfo: {
    nuCpf: string;
    cdPaciente: string;
  } | null;
}

interface Beneficiario {
  cdPaciente: number;
  nmPaciente: string;
  dtNascimento: string;
  nuCpf: string;
}

const TabelaDeAvaliacao: React.FC<TabelaDeAvaliacaoProps> = ({
  criterios,
  nomePaciente,
  beneficiarioInfo,
}) => {
  const [selecoes, setSelecoes] = useState<number[]>(
    new Array(criterios.length).fill(-1)
  );
  const [nomeProfissional, setNomeProfissional] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  // Declare as funções antes de serem usadas
  const handleSelect = (criterioIndex: number, valor: number) => {
    const newSelecoes = [...selecoes];
    newSelecoes[criterioIndex] = valor;
    setSelecoes(newSelecoes);
  };

  const calcularTotal = () => {
    return selecoes.reduce(
      (total, selecao) => total + (selecao >= 0 ? selecao : 0),
      0
    );
  };

  const calcularClassificacao = (total: number): string => {
    if (total <= 5) {
      return "REDE";
    }
    if (total <= 9) {
      return "BAIXA";
    }
    if (total <= 13) {
      return "MÉDIA";
    }
    if (total <= 16) {
      return "ALTA";
    }

    return "SEM INDICACAO DOMICILIAR";
  };

  const getClassBorderColor = (classType: string) => {
    switch (classType) {
      case "BAIXA":
        return "border-blue-500 bg-blue-50 text-blue-800";
      case "REDE":
        return "border-red-500 bg-red-50 text-red-800";
      case "MÉDIA":
        return "border-orange-500 bg-orange-50 text-orange-800";
      case "ALTA":
        return "border-green-500 bg-green-50 text-green-800";
      default:
        return "border-gray-500 bg-gray-50 text-gray-800";
    }
  };

  const handleSalvarAvaliacao = async () => {
    if (!nomePaciente || !beneficiarioInfo) {
      Swal.fire({
        title: "Erro!",
        text: "Por favor, selecione um beneficiário antes de salvar a avaliação.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (selecoes.some((selecao) => selecao === -1)) {
      Swal.fire({
        title: "Atenção!",
        text: "Por favor, preencha todos os critérios antes de salvar.",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (!nomeProfissional.trim()) {
      Swal.fire({
        title: "Atenção!",
        text: "Por favor, informe seu nome como profissional responsável pela avaliação.",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    try {
      setIsSaving(true);
      const token = localStorage.getItem("Frontend");

      if (!token) {
        Swal.fire({
          title: "Erro!",
          text: "Usuário não autenticado. Faça login novamente.",
          icon: "error",
          confirmButtonText: "Ok",
        });
        setIsSaving(false);
        return;
      }

      const total = calcularTotal();
      const classificacao = calcularClassificacao(total);
      const classificacaoDescricao = classificacao;

      const requestData = {
        nuCpf: beneficiarioInfo.nuCpf,
        classificacaoRisco: total,
        classificacaoDescricao: classificacaoDescricao,
        avd: selecoes[0] >= 0 ? selecoes[0] : 0,
        funcaoCognitiva: selecoes[1] >= 0 ? selecoes[1] : 0,
        suporteRespiratorio: selecoes[2] >= 0 ? selecoes[2] : 0,
        incontinenciaUrinaria: selecoes[3] >= 0 ? selecoes[3] : 0,
        usoMedicacao: selecoes[4] >= 0 ? selecoes[4] : 0,
        ulceraPressao: selecoes[5] >= 0 ? selecoes[5] : 0,
        assistenciaEquipeMulti: selecoes[6] >= 0 ? selecoes[6] : 0,
        terapiaNutricional: selecoes[7] >= 0 ? selecoes[7] : 0,
        nomeProfissionalAvaliacao: nomeProfissional,
      };

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };

      const response = await api.post(
        "/sisclinic/sad/avaliacoes",
        requestData,
        config
      );

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          title: "Sucesso!",
          text: "Avaliação salva com sucesso!",
          icon: "success",
          confirmButtonText: "Ok",
        }).then((result) => {
          if (result.isConfirmed) {
            // Redirecionar para Sad_Formulario
            navigate("/Sad_Formulario");
          }
        });
      } else {
        Swal.fire({
          title: "Erro!",
          text: "Erro ao salvar avaliação.",
          icon: "error",
          confirmButtonText: "Entendi",
        });
      }
    } catch (error) {
      console.error("Erro ao salvar avaliação:", error);

      let errorMessage = "Erro ao salvar avaliação.";

      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as any;
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      Swal.fire({
        title: "Erro!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "Entendi",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const total = calcularTotal();
  const classificacao = calcularClassificacao(total);
  const classBorderColor = getClassBorderColor(classificacao.split(" ")[0]);

  return (
    <div>
      <div className="overflow-x-auto sm:-mx-4">
        <div className="inline-block min-w-full sm:px-4">
          <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
                  <th className="border border-gray-300 p-3 text-left">
                    Critério
                  </th>
                  <th className="border border-gray-300 p-3 text-center">0</th>
                  <th className="border border-gray-300 p-3 text-center">1</th>
                  <th className="border border-gray-300 p-3 text-center">2</th>
                  <th className="border border-gray-300 p-3 text-center">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {criterios.map((criterio, index) => (
                  <tr key={index} className="hover:bg-gray-50 even:bg-gray-50">
                    <td className="border border-gray-200 p-3 font-medium">
                      {criterio.nome}
                    </td>
                    {criterio.opcoes.map((opcao, i) => (
                      <td
                        key={i}
                        className={`border border-gray-200 p-3 text-center cursor-pointer transition-colors ${
                          selecoes[index] === i
                            ? "bg-blue-100 text-blue-800 font-semibold"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => handleSelect(index, i)}
                      >
                        {opcao}
                      </td>
                    ))}
                    <td className="border border-gray-200 p-3 text-center font-semibold">
                      {selecoes[index] >= 0 ? selecoes[index] : "–"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nome do Profissional Responsável *
        </label>
        <input
          type="text"
          value={nomeProfissional}
          onChange={(e) => setNomeProfissional(e.target.value)}
          className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          placeholder="Digite seu nome completo"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <div
          className={`p-4 border-2 rounded-lg text-center ${classBorderColor}`}
        >
          <strong className="block text-sm uppercase text-gray-600">
            Total
          </strong>
          <span className="text-2xl font-bold">{total}</span>
        </div>

        <div
          className={`p-4 border-2 rounded-lg text-center ${classBorderColor}`}
        >
          <strong className="block text-sm uppercase text-gray-600">
            Classificação SAD
          </strong>
          <span className="text-xl font-bold">{classificacao}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mt-6 justify-center">
        <button
          className="flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSalvarAvaliacao}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Salvando...
            </>
          ) : (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Salvar Avaliação
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [beneficiarios, setBeneficiarios] = useState<Beneficiario[]>([]);
  const [novoPaciente, setNovoPaciente] = useState({
    nome: "",
    nu_cpf: "",
    cd_paciente: "",
    idade: "",
  });
  const [showBeneficiariosDropdown, setShowBeneficiariosDropdown] =
    useState(false);
  const [filteredBeneficiarios, setFilteredBeneficiarios] = useState<
    Beneficiario[]
  >([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchBeneficiarios = async () => {
      try {
        const token = localStorage.getItem("Frontend");
        const response = await api.get("/sisclinic/pacientes", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data && Array.isArray(response.data)) {
          const uniqueBeneficiarios = response.data.filter(
            (item: Beneficiario, index: number, self: Beneficiario[]) =>
              index === self.findIndex((b) => b.cdPaciente === item.cdPaciente)
          );

          setBeneficiarios(uniqueBeneficiarios);
          setFilteredBeneficiarios(uniqueBeneficiarios);
        }
      } catch (error) {
        console.error("Erro ao buscar beneficiários:", error);
      }
    };

    fetchBeneficiarios();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowBeneficiariosDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (novoPaciente.nome) {
      const filtered = beneficiarios.filter((beneficiario) => {
        if (!beneficiario || !beneficiario.nmPaciente) {
          return false;
        }
        return beneficiario.nmPaciente
          .toLowerCase()
          .includes(novoPaciente.nome.toLowerCase());
      });

      const uniqueFiltered = filtered.filter(
        (item, index, self) =>
          index === self.findIndex((b) => b.cdPaciente === item.cdPaciente)
      );

      setFilteredBeneficiarios(uniqueFiltered);
    } else {
      setFilteredBeneficiarios(beneficiarios);
    }
  }, [novoPaciente.nome, beneficiarios]);

  const calcularIdade = (dataNascimento: string): number => {
    try {
      if (!dataNascimento) return 0;

      const nascimento = new Date(dataNascimento);
      const hoje = new Date();
      let idade = hoje.getFullYear() - nascimento.getFullYear();
      const mes = hoje.getMonth() - nascimento.getMonth();
      if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
        idade--;
      }
      return idade;
    } catch (error) {
      return 0;
    }
  };

  const handleBeneficiarySelect = (beneficiario: Beneficiario) => {
    const idadeCalculada = calcularIdade(beneficiario.dtNascimento);
    setNovoPaciente({
      ...novoPaciente,
      nome: beneficiario.nmPaciente,
      idade: idadeCalculada.toString(),
      nu_cpf: beneficiario.nuCpf,
      cd_paciente: beneficiario.cdPaciente.toString(),
    });
    setShowBeneficiariosDropdown(false);
  };

  const criterios = [
    {
      nome: "AVD's (Atividades de Vida Diária)",
      opcoes: [
        "Sai de casa para realizar atividades sociais e de saúde.",
        "Alguma dificuldade nas AVD's. Deambula com auxílio de outra pessoa.",
        "Restrito ao leito. Dependência total.",
      ],
    },
    {
      nome: "Função Cognitiva",
      opcoes: [
        "Consegue manter corretamente uma conversação.",
        "Desorientação no tempo, esquecimentos ocasionais.",
        "Não pode manter uma conversação lógica.",
      ],
    },
    {
      nome: "Suporte Respiratório",
      opcoes: ["Sem suporte.", "Oxigênio intermitente.", "Oxigênio contínuo."],
    },
    {
      nome: "Incontinência Urinária",
      opcoes: [
        "Continente.",
        "Incontinência ocasional.",
        "Fralda ou dispositivo jontex.",
      ],
    },
    {
      nome: "Uso de Medicação",
      opcoes: ["Oral.", "Subcutânea.", "SNE/GTT."],
    },
    {
      nome: "Úlcera de Pressão",
      opcoes: [
        "Ausente sans fator de risco.",
        "Ausente com fator de risco.",
        "Presente.",
      ],
    },
    {
      nome: "Assistência Equipe Multidisciplinar",
      opcoes: [
        "Não necessita.",
        "Acompanhamento de até 02 especialidades.",
        "Acompanhamento de 03 a 04 especialidades.",
      ],
    },
    {
      nome: "Terapia Nutricional",
      opcoes: [
        "Não necessita.",
        "Suplementação oral.",
        "SNE/GTT gastroduodenal ou jejunostomia.",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="flex justify-center items-center mb-8">
        <motion.img
          src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
          alt="Logo AFRAFEP"
          className="w-60 h-auto"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      </div>

      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Avaliação SAD</h1>
              <p className="text-blue-100">Sistema de Avaliação Domiciliar</p>
            </div>
            <button
              onClick={() => window.history.back()}
              className="flex items-center px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Voltar
            </button>
          </div>
        </div>

        <div className="p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            Dados do Paciente
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="relative" ref={dropdownRef}>
              <label
                htmlFor="nome"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Nome do Paciente *
              </label>
              <div className="relative">
                <div className="flex items-center">
                  <input
                    id="nome"
                    type="text"
                    value={novoPaciente.nome}
                    onChange={(e) => {
                      setNovoPaciente({
                        ...novoPaciente,
                        nome: e.target.value,
                        nu_cpf: "",
                        cd_paciente: "",
                        idade: "",
                      });
                    }}
                    onFocus={() => setShowBeneficiariosDropdown(true)}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 pr-10"
                    placeholder="Digite ou selecione um paciente"
                    autoComplete="off"
                  />
                  <BsChevronDown
                    className="absolute right-3 text-gray-400 cursor-pointer"
                    onClick={() =>
                      setShowBeneficiariosDropdown(!showBeneficiariosDropdown)
                    }
                  />
                </div>

                {showBeneficiariosDropdown &&
                  filteredBeneficiarios.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredBeneficiarios.map((beneficiario) => {
                        if (!beneficiario) return null;

                        return (
                          <div
                            key={`${beneficiario.cdPaciente}-${beneficiario.nuCpf}`}
                            className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onClick={() =>
                              handleBeneficiarySelect(beneficiario)
                            }
                          >
                            <div className="font-medium">
                              {beneficiario.nmPaciente || "Nome não disponível"}
                            </div>
                            <div className="text-sm text-gray-800">
                              CPF: {beneficiario.nuCpf || "Não informado"} |
                              Código:{" "}
                              {beneficiario.cdPaciente || "Não informado"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                {showBeneficiariosDropdown &&
                  filteredBeneficiarios.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                      <div className="px-4 py-3 text-gray-500 text-center">
                        Nenhum paciente encontrado
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div>
              <label
                htmlFor="idade"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Idade
              </label>
              <input
                type="text"
                id="idade"
                value={novoPaciente.idade}
                readOnly
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </div>

          <TabelaDeAvaliacao
            criterios={criterios}
            nomePaciente={novoPaciente.nome}
            beneficiarioInfo={
              novoPaciente.nu_cpf
                ? {
                    nuCpf: novoPaciente.nu_cpf,
                    cdPaciente: novoPaciente.cd_paciente,
                  }
                : null
            }
          />
        </div>
      </div>
    </div>
  );
};

export default App;
